import { types, getParent } from 'mobx-state-tree';
import Path, { type IPath } from './path';
import { TYPES, SYMBOLS, TYPE_INFO } from './mpc_config';
import { constraintDisablesPath } from './mpc_utils';
import type { IObjectType } from './object_type';
import type { IDependency } from './dependency';
import type { IInheritance } from './inheritance';

// A path element is a reference to either a dependency or an inheritance.
type PathElement = IDependency | IInheritance;

/**
 * The MultiplePropagationConstraint instance interface.
 *
 * HAND-DECLARED (not `Instance<typeof MultiplePropagationConstraint>`) to anchor
 * the recursion: several views take another constraint as a parameter typed with
 * this same interface, which would otherwise be a self-reference in the model's
 * own initializer.
 */
export interface IMultiplePropagationConstraint {
  readonly id: string;
  readonly type: 'EQUAL_TO' | 'NOT_EQUAL_TO';
  readonly paths: IPath[];
  asString(): string;
  readonly displayString: string;
  readonly objectType: IObjectType;
  samePaths(other: IMultiplePropagationConstraint): boolean;
  sameTails(other: IMultiplePropagationConstraint): boolean;
  equals(other: IMultiplePropagationConstraint): boolean;
  conflicts(other: IMultiplePropagationConstraint): boolean;
  readonly constrainedObject: IObjectType | undefined;
  readonly constrainedObjectName: string;
  readonly primaryPath: IPath;
  readonly secondaryPath: IPath;
  dependsOnEdge(element: PathElement): boolean;
  disablesPath(elements: readonly PathElement[]): boolean;
  setType(type: 'EQUAL_TO' | 'NOT_EQUAL_TO'): void;
}

/**
 * A MERODE multiple propagation constraint.
 */
const MultiplePropagationConstraint = types
  .model('MultiplePropagationConstraint', {
    id: types.identifier,
    type: types.enumeration(TYPES),
    paths: types.array(Path),
  })
  .views(self0 => {
    const self = self0 as unknown as IMultiplePropagationConstraint;
    return {
    // Human-readable label, e.g. for `${mpc}` interpolation in messages.
    // (MST 7 forbids declaring a view named `toString`, so this is a plain view.)
    asString() {
      return `Constraint on ${self.constrainedObject!.name} in ${self.objectType.name}`;
    },
    // Used in the logger and the report view
    get displayString() {
      return `MPC ${self.objectType.name}.${self.primaryPath.information} ${SYMBOLS[self.type]} ${
        self.objectType.name
      }.${self.secondaryPath.information}`;
    },
    // Convenience method to get the object type
    get objectType(): IObjectType {
      return getParent(self0, 2);
    },
    // does the constraint have the same paths as the given other constraint?
    samePaths(other: IMultiplePropagationConstraint) {
      return (
        (self.primaryPath.equals(other.primaryPath) &&
          self.secondaryPath.equals(other.secondaryPath)) ||
        (self.secondaryPath.equals(other.primaryPath) &&
          self.primaryPath.equals(other.secondaryPath))
      );
    },
    // does the constraint have the same tails as the given other constraint?
    sameTails(other: IMultiplePropagationConstraint) {
      return (
        (self.primaryPath.isTailOf(other.primaryPath.elements) &&
          self.secondaryPath.isTailOf(other.secondaryPath.elements)) ||
        (self.primaryPath.isTailOf(other.secondaryPath.elements) &&
          self.secondaryPath.isTailOf(other.primaryPath.elements))
      );
    },
    // does the constraint equal the given other constraint
    equals(other: IMultiplePropagationConstraint) {
      return (
        self.objectType === other.objectType && self.type === other.type && self.samePaths(other)
      );
    },
    // does the constraint conflict with the given other constraint?
    conflicts(other: IMultiplePropagationConstraint) {
      if (self.type === TYPE_INFO.EQUAL_TO.key) {
        // A = B conflicts with A != B and A.t != B.t
        return (
          other.type === TYPE_INFO.NOT_EQUAL_TO.key &&
          (self.samePaths(other) || self.sameTails(other))
        );
      } else if (self.type === TYPE_INFO.NOT_EQUAL_TO.key) {
        // A.t != B.t conflicts with A.t = B.t and A = B
        return (
          other.type === TYPE_INFO.EQUAL_TO.key && (self.samePaths(other) || other.sameTails(self))
        );
      }
      return false;
    },
    // get the constrained object
    get constrainedObject() {
      return self.primaryPath.firstObject;
    },
    // get the name of the constrained object
    get constrainedObjectName() {
      return self.constrainedObject!.name;
    },
    // get the primary path. A well-formed MPC always has a primary and a
    // secondary path, so callers treat these as non-null.
    get primaryPath(): IPath {
      return self.paths.find(p => p.primary) as IPath;
    },
    // get the secondary path
    get secondaryPath(): IPath {
      return self.paths.find(p => !p.primary) as IPath;
    },
    // Does the constraint depend on the given element
    // Be future proof, could be a inheritance relation iso dependency in the future
    dependsOnEdge(element: PathElement) {
      return self.paths.some(p => p.includes(element));
    },
    // Does the constraint disables the given path?
    disablesPath(elements: readonly PathElement[]) {
      // The shared helper types path elements structurally (just `{ id }`); our
      // concrete element union is narrower, so we adapt at this single boundary.
      return constraintDisablesPath(
        self as unknown as Parameters<typeof constraintDisablesPath>[0],
        elements
      );
    },
    };
  })
  .actions(self => ({
    setType(type: 'EQUAL_TO' | 'NOT_EQUAL_TO') {
      self.type = type;
    },
  }));

export default MultiplePropagationConstraint;
